// app/components/utils/exportUtils.js

export function downloadTxt(letterText, studentName, templateId) {
  if (!letterText) return;
  const blob = new Blob([letterText], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), {
    href: url,
    download: `recommendation_${(studentName || "student").replace(/[^\w-]+/g, "_")}_${templateId}.txt`,
  });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadDocx(docxBase64, studentName, templateId) {
  if (!docxBase64) return;
  const bin = Uint8Array.from(atob(docxBase64), (c) => c.charCodeAt(0));
  const blob = new Blob([bin], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), {
    href: url,
    download: `recommendation_${(studentName || "student").replace(/[^\w-]+/g, "_")}_${templateId}.docx`,
  });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadPdf(letterText, studentName, templateId, logoDataUrl = "", logoAlign = "left", logoWidth = 140) {
  if (!letterText) return;
  const { jsPDF } = await import("jspdf");

  const loadImageMeta = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
      img.onerror = reject;
      img.src = dataUrl;
    });

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 72;
  const maxWidth = pageWidth - margin * 2;
  const lineH = 16;

  doc.setFont("Times", "Normal");
  doc.setFontSize(12);

  let y = margin;

  // Logo at top (optional)
  if (logoDataUrl) {
    try {
      const typeMatch = /^data:image\/(png|jpeg|jpg|webp|svg\+xml)/i.exec(logoDataUrl);
      const format = !typeMatch
        ? "PNG"
        : typeMatch[1].toLowerCase().startsWith("jp")
        ? "JPEG"
        : typeMatch[1].toLowerCase().startsWith("png")
        ? "PNG"
        : typeMatch[1].toLowerCase().startsWith("webp")
        ? "WEBP"
        : "PNG";

      const meta = await loadImageMeta(logoDataUrl);
      const targetW = Math.max(40, Math.min(Number(logoWidth || 140), 400));
      const scale = targetW / meta.w;
      const targetH = meta.h * scale;

      let x = margin;
      if (logoAlign === "center") x = (pageWidth - targetW) / 2;
      else if (logoAlign === "right") x = pageWidth - margin - targetW;

      doc.addImage(logoDataUrl, format, x, y, targetW, targetH, undefined, "FAST");
      y += targetH + 10;
    } catch {
      // ignore logo errors
    }
  }

  const blocks = String(letterText).replace(/\r\n/g, "\n").split(/\n{2,}/g);

  for (const block of blocks) {
    const isSubject = /^Subject:\s/i.test(block.trim());
    const lines = block.split("\n");

    if (isSubject) doc.setFont("Times", "Bold");
    for (let i = 0; i < lines.length; i++) {
      const chunks = doc.splitTextToSize(lines[i], maxWidth);
      for (const chunk of chunks) {
        if (y + lineH > pageHeight - margin) {
          doc.addPage();
          doc.setFont("Times", isSubject ? "Bold" : "Normal");
          y = margin;
        }
        doc.text(chunk, margin, y);
        y += lineH;
      }
    }
    if (isSubject) doc.setFont("Times", "Normal");
    y += lineH;
    if (y > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  }

  doc.save(`recommendation_${(studentName || "student").replace(/[^\w-]+/g, "_")}_${templateId}.pdf`);
}

export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}